import { Button, Input, Select } from "../../custom";
import { ReactComponent as Upload } from "../../assets/upload.svg";
import styles from "./styles.module.scss";
import { Form, Formik, FormikValues } from "formik";
import React, { FC, useEffect, useId, useMemo, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  createUpdatePersonalInfo,
  getAllDisability,
  getAllGender,
  getAllMaritalStatus,
  getAllReligion,
  getAllTitle,
  getCountries,
  getLgaByStateId,
  getPersonalInfoByApplicantId,
  getStateByCountryId,
} from "../../utils/request";
import { useAtom, useAtomValue } from "jotai";
import { pageIndexAtom, userAtom } from "../../utils/store";
import { App, Spin } from "antd";
import { formatDate } from "../../utils/formatDate";
import { object } from "yup";
import { validator } from "../../utils/validator";
import { useFileValidator } from "../../hooks/useFileValidator";
import { INSTITUTION_NAME } from "../../utils/constants";
import { useGetApplicantDetailsQuery } from "../../hooks/api/useGetApplicantDetailsQuery";

interface ComponentProps {
  isEdit?: boolean;
  onClose: () => void;
}

interface PersonalInfoInit {
  firstName: string;
  middleName: string;
  lastName: string;
  gender: string;
  maritalStatus: string;
  religion: string;
  phoneNumber: string;
  dob: string;
  stateOfOrigin: string;
  email: string;
  address: string;
  country: string;
  city: string;
  zipCode: string;
  disability: string;
  description: string;
  lga: string;
  title: string;
}

const getSelectValue = (
  value: unknown,
  fallback?: string | number | null,
) => {
  const normalizedValue = value?.toString().trim();

  if (normalizedValue && normalizedValue !== "0") {
    return normalizedValue;
  }

  const normalizedFallback = fallback?.toString().trim();
  if (normalizedFallback && normalizedFallback !== "0") {
    return normalizedFallback;
  }

  return "0";
};

const getInitialSelectValue = (value?: string | number | null) => {
  const normalizedValue = value?.toString().trim();
  return normalizedValue && normalizedValue !== "0" ? normalizedValue : "";
};

const getValidDobValue = (date?: string) => {
  if (!date) return "";
  if (date.startsWith("0001-01-01")) return "";
  return formatDate(date);
};

// Some devices (e.g. phone cameras) produce uppercase extensions like
// ".JPG"/".PNG". The backend's extension check is case-sensitive, so we
// normalize to a lowercase extension here to avoid a false "Invalid file
// extension" rejection on an otherwise valid image.
const getExtensionFromMimeType = (mimeType: string) =>
  mimeType === "image/png" ? "png" : "jpg";

const normalizeImageFileName = (file: File) => {
  const extension = getExtensionFromMimeType(file.type);
  const baseName = file.name.replace(/\.[^./\\]+$/, "").trim() || "passport";

  return new File([file], `${baseName}.${extension}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
};

const NO_DISABILITY_LABELS = new Set([
  "none",
  "no",
  "nil",
  "n/a",
  "na",
  "not applicable",
  "no disability",
  "not disabled",
]);

const isNoDisabilitySelected = (name?: string) =>
  !name || NO_DISABILITY_LABELS.has(name.trim().toLowerCase());

const PersonalInfo: FC<ComponentProps> = ({ isEdit, onClose }) => {
  const { validateFile } = useFileValidator();
  const [countryId, setCountryId] = useState(0);
  const [stateId, setStateId] = useState(0);
  const [shouldContinue, setShouldContinue] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const passportInputId = useId();

  const storedUser = useAtomValue(userAtom);
  const { applicantDetails } = useGetApplicantDetailsQuery({
    applicantId: storedUser?.applicantId,
  });
  const user = applicantDetails ?? storedUser;
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);

  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const queries = useQueries({
    queries: [
      {
        queryKey: ["get-countries"],
        queryFn: getCountries,
        meta: { institution: INSTITUTION_NAME }, // Header support
      },
      {
        queryKey: ["get-state-id", countryId],
        queryFn: () => getStateByCountryId(countryId),
        enabled: !!countryId && countryId > 0,
        refetchOnWindowFocus: true,
        meta: { institution: INSTITUTION_NAME },
      },
      {
        queryKey: ["get-marital-status"],
        queryFn: getAllMaritalStatus,
        meta: { institution: INSTITUTION_NAME },
      },
      {
        queryKey: ["get-disability"],
        queryFn: getAllDisability,
        meta: { institution: INSTITUTION_NAME },
      },
      {
        queryKey: ["get-religion"],
        queryFn: getAllReligion,
        meta: { institution: INSTITUTION_NAME },
      },
      {
        queryKey: ["get-lga-id", stateId],
        queryFn: () => getLgaByStateId(stateId),
        enabled: !!stateId && stateId > 0,
        refetchOnWindowFocus: true,
        meta: { institution: INSTITUTION_NAME },
      },
      {
        queryKey: ["get-personal-info-id", user?.applicantId],
        queryFn: () => getPersonalInfoByApplicantId(user?.applicantId ?? ""),
        enabled: !!user?.applicantId,
        meta: { institution: INSTITUTION_NAME },
      },
      {
        queryKey: ["get-all-gender"],
        queryFn: getAllGender,
        meta: { institution: INSTITUTION_NAME },
      },
      {
        queryKey: ["get-all-title"],
        queryFn: getAllTitle,
        meta: { institution: INSTITUTION_NAME },
      },
    ],
  });

  const countriesQuery = queries[0];
  const stateQuery = queries[1];
  const maritalStatusQuery = queries[2];
  const disabilityQuery = queries[3];
  const religionQuery = queries[4];
  const LGAQuery = queries[5];
  const personalInfoQuery = queries[6];
  const genderQuery = queries[7];
  const titleQuery = queries[8];

  const {
    data: personalInfo,
    isLoading: isPersonalInfoLoading,
    isError: isPersonalInfoError,
    error: personalInfoError,
  } = personalInfoQuery;

  const { data: title } = titleQuery;

  const countryData = useMemo(() => countriesQuery?.data?.data ?? [], [
    countriesQuery?.data?.data,
  ]);
  const stateData = useMemo(() => stateQuery?.data?.data ?? [], [
    stateQuery?.data?.data,
  ]);
  const maritalStatusData = maritalStatusQuery?.data?.data ?? [];
  const disabilityData = disabilityQuery?.data?.data ?? [];
  const religionData = religionQuery?.data?.data ?? [];
  const lgaData = LGAQuery?.data?.data ?? [];
  const personalInfoData = personalInfo?.data ?? ({} as PersonalInfo);
  const genderData = genderQuery?.data?.data ?? [];
  const titleData = title?.data ?? [];

  const createUpdatePersonalInfoMutation = useMutation({
    mutationFn: createUpdatePersonalInfo,
  });

  const handlePersonalInfo = async (values: FormikValues) => {
    const formData = new FormData();

    const genderId = getSelectValue(values.gender, personalInfoData?.genderId);
    const maritalStatusId = getSelectValue(
      values.maritalStatus,
      personalInfoData?.maritalStatusId,
    );
    const religionId = getSelectValue(
      values.religion,
      personalInfoData?.religionId,
    );
    const countryValue = getSelectValue(
      values.country,
      countryId || personalInfoData?.countryId,
    );
    const lgaId = getSelectValue(values.lga, personalInfoData?.lgaId);
    const stateOfOriginId = getSelectValue(
      values.stateOfOrigin,
      stateId || personalInfoData?.stateOfOriginId,
    );
    const disabilityId = getSelectValue(
      values.disability,
      personalInfoData?.disabilityId,
    );
    const titleId = getSelectValue(values.title, personalInfoData?.titleId);

    formData.append("Id", personalInfoData?.id?.toString() ?? "0");
    formData.append("ApplicantId", user?.applicantId ?? "");
    formData.append("FName", values.firstName);
    formData.append("MNane", values.middleName || "");
    formData.append("LName", values.lastName);
    formData.append("GenderId", genderId);
    formData.append("MaritalStatusId", maritalStatusId);
    formData.append("ReligionId", religionId);
    formData.append("PhoneNo", values.phoneNumber);
    formData.append("DateOfBirth", values.dob);
    formData.append("CountryId", countryValue);
    formData.append("LGAId", lgaId);
    formData.append("StateOfOriginId", stateOfOriginId);
    formData.append("Address", values.address);
    formData.append("Email", values.email);
    formData.append("ZipCode", values.zipCode || "0");
    formData.append("DisabilityId", disabilityId);
    formData.append("DescribeDisability", values.description || "");
    formData.append("IsActive", String(true));
    formData.append("TitleId", titleId);
    formData.append("City", values.city);

    // Only append image if there's a new upload
    if (uploadedImage) {
      formData.append("Image", uploadedImage);
    }

    try {
      await createUpdatePersonalInfoMutation?.mutateAsync(formData, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          queryClient.refetchQueries({ queryKey: ["get-personal-info-id"] });

          // Reset the image upload state
          setUploadedImage(null);

          onClose();

          if (shouldContinue) {
            // Add a small delay to ensure state updates before navigation
            setTimeout(() => {
              handleNextNavigation();
            }, 500);
          }
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message || "An error occurred",
      });
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const { isValidType, isValidSize } = validateFile(file, {
        allowedTypes: ["image/jpeg", "image/png", "image/jpg"],
        maxSizeMB: 2,
      });
      if (isValidType && isValidSize) {
        setUploadedImage(normalizeImageFileName(file));
      } else {
        setUploadedImage(null);
        notification.error({
          message: "Invalid file",
          description:
            "Please upload a valid image file (JPEG, PNG, JPG) under 2 MB.",
        });
      }
    }
  };

  const countryOptions = countryData.map((item: any) => (
    <option key={item?.id} value={item?.id}>
      {item?.countryName}
    </option>
  ));

  const stateOptions = stateData.map((item: any) => (
    <option key={item?.id} value={item?.id}>
      {item?.stateName}
    </option>
  ));

  const lgaOptions = lgaData.map((item: any) => (
    <option key={item?.id} value={item?.id}>
      {item?.lgaName}
    </option>
  ));

  const titleOptions = titleData.map((item: any) => (
    <option key={item?.id} value={item?.id}>
      {item?.titleName}
    </option>
  ));

  const maritalStatusOptions = maritalStatusData.map((item: any) => (
    <option key={item?.id} value={item?.id}>
      {item?.statusName}
    </option>
  ));

  const disabilityOptions = disabilityData.map((item: any) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  const religionOptions = religionData.map((item: any) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  const genderOptions = genderData.map((item: any) => (
    <option key={item?.id} value={item?.id}>
      {item?.genderName}
    </option>
  ));

  // ...existing code...
  useEffect(() => {
    if (personalInfoData?.countryId && personalInfoData.countryId > 0) {
      setCountryId(personalInfoData.countryId);
    } else if (user?.countryId && user.countryId > 0) {
      // Fall back to the country selected at registration
      setCountryId(user.countryId);
    } else if (countryData.length > 0) {
      // Default to the first country (e.g., Nigeria) if no saved data
      setCountryId(countryData[0]?.id);
    }
  }, [personalInfoData?.countryId, user?.countryId, countryData]);

  useEffect(() => {
    if (
      personalInfoData?.stateOfOriginId &&
      personalInfoData.stateOfOriginId > 0
    ) {
      setStateId(personalInfoData.stateOfOriginId);
    } else if (stateData.length > 0) {
      // Default to the first state if no saved data
      setStateId(stateData[0]?.id);
    }
  }, [personalInfoData?.stateOfOriginId, stateData]);
  // ...existing code...

  const validationSchema = object().shape({
    firstName: validator.firstName,
    middleName: validator.middleName,
    lastName: validator.lastName,
    gender: validator.gender,
    maritalStatus: validator.maritalStatus,
    religion: validator.religion,
    phoneNumber: validator.phoneNumber,
    dob: validator.dob,
    stateOfOrigin: validator.stateOfOrigin,
    email: validator.email,
    address: validator.address,
    country: validator.country,
    city: validator.city,
    zipCode: validator.zipCode,
    disability: validator.disability,
    title: validator.title,
    lga: validator.lga,
  });

  const handleNextNavigation = () => {
    setPageIndex(pageIndex + 1);
  };

  if (isPersonalInfoLoading) {
    return (
      <div className={styles.loader}>
        <Spin size="large" />
      </div>
    );
  }

  if (isPersonalInfoError) {
    return <div>{personalInfoError?.message}</div>;
  }

  return (
    <>
      <section className={styles.section}>
        <h2>Personal Information</h2>
        <p>All fields marked with a (*) are required.</p>
      </section>

      <hr />

      <div className={styles.section}>
        <section className={styles.upload}>
          <div>
            <p>Upload your passport photograph</p>
            <h5>
              <b>Requirements:</b>
            </h5>

            <ul>
              <li>Picture must be clear</li>
              <li>Must be on a white background</li>
              <li>Accepted formats: JPG, JPEG, PNG</li>
              <li>Max photo size 2MB</li>
              <li>Must not be older than 6 months</li>
            </ul>
          </div>

          <label htmlFor={passportInputId} className={styles.uploadCta}>
            {uploadedImage ? (
              <img
                src={URL.createObjectURL(uploadedImage)}
                alt="Uploaded"
                width="100"
                height="100"
              />
            ) : personalInfoData?.imageUrl ? (
              <img
                src={personalInfoData?.imageUrl}
                alt="Uploaded"
                width="100"
                height="100"
              />
            ) : (
              <Upload />
            )}

            <div>
              <input
                id={passportInputId}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                className={styles.hiddenFileInput}
                onChange={handleImageUpload}
              />
              <span>
                {uploadedImage ? "Change Passport" : "Upload Passport"}
              </span>
            </div>
          </label>
        </section>
        <br />

        <Formik<PersonalInfoInit>
          enableReinitialize
          initialValues={{
            firstName: personalInfoData?.fName ?? user?.firstName,
            middleName: personalInfoData?.mNane ?? "",
            lastName: personalInfoData?.lName ?? user?.lastName,
            gender: getInitialSelectValue(personalInfoData?.genderId),
            maritalStatus: getInitialSelectValue(
              personalInfoData?.maritalStatusId,
            ),
            religion: getInitialSelectValue(personalInfoData?.religionId),
            phoneNumber: personalInfoData?.phoneNo ?? user?.phoneNo,
            dob: getValidDobValue(personalInfoData?.dateOfBirth),
            stateOfOrigin: getInitialSelectValue(
              personalInfoData?.stateOfOriginId,
            ),
            email: personalInfoData?.email ?? user?.email,
            address: personalInfoData?.address ?? "",
            country: getInitialSelectValue(
              personalInfoData?.countryId ?? user?.countryId,
            ),
            zipCode: personalInfoData?.zipCode?.toString() ?? "",
            disability: getInitialSelectValue(personalInfoData?.disabilityId),
            description: personalInfoData?.describeDisability ?? "",
            lga: getInitialSelectValue(personalInfoData?.lgaId),
            title: getInitialSelectValue(personalInfoData?.titleId),
            city: personalInfoData?.city ?? "",
          }}
          validationSchema={validationSchema}
          // onSubmit={(values) => {
          //   if (uploadedImage || personalInfoData?.imageUrl) {
          //     handlePersonalInfo(values);
          //   } else {
          //     notification.error({
          //       message: "Passport photograph required",
          //       description:
          //         "Please upload your passport photograph to proceed.",
          //     });
          //   }
          // }}
          onSubmit={(values) => {
            handlePersonalInfo(values);
          }}
        >
          {({
            setFieldValue,
            values,
            submitForm,
            setTouched,
          }) => {
            const touchAllFields = () => {
              setTouched({
                title: true,
                firstName: true,
                middleName: true,
                lastName: true,
                gender: true,
                maritalStatus: true,
                religion: true,
                dob: true,
                country: true,
                stateOfOrigin: true,
                lga: true,
                email: true,
                address: true,
                phoneNumber: true,
                city: true,
                zipCode: true,
                disability: true,
                description: true,
              });
            };
            
            return (
              <Form>
                <section className={styles.grid}>
                  <Select
                    name="title"
                    label="Title"
                    placeholder="Enter title"
                    options={titleOptions}
                    value={values.title}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setFieldValue("title", e.target.value)
                    }
                    asterisk
                  />
                  {/* Same for gender and maritalStatus - just use handleChange */}
                  <Input
                    type="text"
                    name="firstName"
                    label="First Name"
                    placeholder="Enter first name"
                    asterisk
                  />
                  <Input
                    type="text"
                    name="middleName"
                    label="Middle Name"
                    placeholder="Enter middle name"
                  />
                  <Input
                    type="text"
                    name="lastName"
                    label="Last Name"
                    placeholder="Enter last name"
                    asterisk
                  />
                  <Select
                    name="gender"
                    label="Gender"
                    placeholder="Enter gender"
                    options={genderOptions}
                    value={values.gender}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setFieldValue("gender", e.target.value)
                    }
                    asterisk
                  />
                  <Select
                    name="maritalStatus"
                    label="Marital Status"
                    placeholder="Enter marital status"
                    options={maritalStatusOptions}
                    value={values.maritalStatus}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setFieldValue("maritalStatus", e.target.value)
                    }
                    asterisk
                  />
                  <Select
                    name="religion"
                    label="Religion"
                    placeholder="Enter religion"
                    options={religionOptions}
                    value={values.religion}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setFieldValue("religion", e.target.value)
                    }
                    asterisk
                  />

                  <Input
                    type="date"
                    name="dob"
                    label="Date of Birth"
                    placeholder="Enter date of birth"
                    asterisk
                  />

                  {/* Country */}
                  <Select
                    name="country"
                    label="Country"
                    placeholder="Enter country"
                    options={countryOptions}
                    value={values.country}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const newCountryId = Number(e.target.value);
                      setCountryId(newCountryId);
                      setFieldValue("country", String(newCountryId));
                      setStateId(0);
                      setFieldValue("stateOfOrigin", "");
                      setFieldValue("lga", "");
                    }}
                    asterisk
                  />

                  {/* State */}
                  <Select
                    name="stateOfOrigin"
                    label="State Of Origin"
                    placeholder="Select state"
                    options={stateOptions}
                    value={values.stateOfOrigin}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const newStateId = Number(e.target.value);
                      setStateId(newStateId);
                      setFieldValue("stateOfOrigin", String(newStateId));
                      setFieldValue("lga", "");
                    }}
                    asterisk
                  />

                  {/* LGA */}
                  <Select
                    name="lga"
                    label="LGA"
                    placeholder="Select LGA"
                    options={lgaOptions}
                    value={values.lga || ""}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setFieldValue("lga", e.target.value);
                    }}
                    asterisk
                  />

                  <Input
                    name="email"
                    label="Email"
                    placeholder="Enter email"
                    readOnly
                    asterisk
                  />
                </section>

                <div className={styles.noGrid}>
                  <Input
                    type="textarea"
                    name="address"
                    label="Address"
                    placeholder="Enter address"
                    asterisk
                  />
                </div>

                <section className={styles.grid}>
                  <Input
                    name="phoneNumber"
                    label="Phone Number"
                    placeholder="Enter phone number"
                    asterisk
                  />

                  <Input name="city" label="City" placeholder="Enter city" asterisk />

                  <Input
                    name="zipCode"
                    label="Zip Code"
                    placeholder="Enter zip code"
                  />

                  <Select
                    name="disability"
                    label="Disability"
                    placeholder="Select disability"
                    options={disabilityOptions}
                    value={values.disability}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setFieldValue("disability", e.target.value);
                    }}
                    asterisk
                  />
                </section>

                {!!values.disability &&
                  !isNoDisabilitySelected(
                    disabilityData?.find(
                      (item: any) => item?.id === Number(values.disability),
                    )?.name,
                  ) && (
                    <div className={styles.noGrid}>
                      <Input
                        type="textarea"
                        name="description"
                        label="Describe in details your disability"
                        placeholder="Type here..."
                      />
                    </div>
                  )}

                <div className={styles.btnGroup}>
                  {/* <Button
                    className={styles.saveBtn}
                    text="Save"
                    type="button"
                    onClick={() => {
                      setShouldContinue(false);
                      touchAllFields();
                      submitForm();
                    }}
                    isLoading={createUpdatePersonalInfoMutation.isPending}
                  /> */}
                  <Button
                    text="Save and Continue"
                    type="button"
                    onClick={() => {
                      setShouldContinue(true);
                      touchAllFields();
                      submitForm();
                    }}
                    isLoading={createUpdatePersonalInfoMutation.isPending}
                  />
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default PersonalInfo;
